import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BadgePercent, ChevronRight, Ticket } from "lucide-react";
import { api } from "../../lib/api";
import { useTenant } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import RestaurantCard from "../../components/RestaurantCard";
import VoiceSearch from "../../components/VoiceSearch";
import CustomerHeader from "../../components/CustomerHeader";
import BannerCarousel from "../../components/BannerCarousel";
import MarketplaceHome from "../../components/MarketplaceHome";
import GroupCategoryChipRow from "../../components/GroupCategoryChipRow";
import { CardSkeleton } from "../../components/Loaders";
import { t } from "../../lib/i18n";
import { isFoodShop } from "../../lib/foodCategory";
import { THEME_PRESETS } from "../../lib/themes";
import { poweredByBrand } from "../../lib/utils";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import BkStoresView from "./blinkit/BkStoresView";
import LseHome from "../../components/lse/LseHome";

// ``/food`` is the Shops tab. Blinkit-theme tenants get the Blinkit stores list here (their home is ``/``); every
// other theme keeps this page exactly as it was, including the Amazon / Flipkart marketplace homes.
export default function HomePage() {
    return useIsBlinkit() ? <BkStoresView /> : <ClassicHomePage />;
}

function ClassicHomePage() {
    const { tenant, lang } = useTenant();
    const { user } = useAuth();
    const [feed, setFeed] = useState(null);
    const [foodTypeFilter, setFoodTypeFilter] = useState(null);
    const [verticals, setVerticals] = useState([]);

    // Right after login the home page is the landing route. If the user picked
    // an address while anonymous (stored in localStorage by AddressPickerModal),
    // commit it now that we have a token.
    useEffect(() => {
        if (!user) return;
        const raw = localStorage.getItem("bizil_pending_address");
        if (!raw) return;
        try {
            const body = JSON.parse(raw);
            api.post("/addresses", body)
                .then(() => { localStorage.removeItem("bizil_pending_address"); })
                .catch(() => { /* leave it so user can retry */ });
        } catch { localStorage.removeItem("bizil_pending_address"); }
    }, [user]);

    useEffect(() => {
        // Fetch the customer's default address (if any) so we can resolve their
        // zone server-side and only show zone-targeted banners that apply to them.
        let cancelled = false;
        (async () => {
            let q = "";
            try {
                const { data } = await api.get("/addresses");
                const def = (data || []).find((a) => a.is_default) || (data || [])[0];
                if (def?.lat != null && def?.lng != null) q = `?lat=${def.lat}&lng=${def.lng}`;
            } catch { /* anonymous user */ }
            try {
                const { data } = await api.get(`/feed${q}`);
                if (!cancelled) setFeed(data);
            } catch {
                if (!cancelled) setFeed({ featured_restaurants: [], nearby_restaurants: [], coupons: [], banners: [] });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        // Fetch the business-category catalog once so we can resolve every shop's
        // vertical (Food / Services / Grocery / etc.) and filter the Food page
        // strictly to food shops — no Home Services contamination.
        api.get("/business-categories")
            .then(({ data }) => setVerticals(data || []))
            .catch(() => setVerticals([]));
    }, []);

    const filterFood = (list) => {
        if (!Array.isArray(list)) return [];
        // Veg / non-veg only makes sense for food shops. When the
        // customer has *not* selected a food-type pill we return the
        // full list so general-marketplace tenants (electronics +
        // groceries + services + food) don't hide their non-food
        // shops on the home page. Once Veg or Non-veg is active we
        // restrict to food shops that also match the picked type —
        // any non-food shop is inherently ineligible.
        if (!foodTypeFilter) return list;
        const onlyFood = list.filter((r) => isFoodShop(r, verticals));
        return onlyFood.filter((r) => r.food_type === foodTypeFilter || r.food_type === "VEG_NON_VEG");
    };

    return (
        <MarketplaceHomeOrDefault feed={feed} verticals={verticals} tenant={tenant}
                                  filterFood={filterFood} foodTypeFilter={foodTypeFilter}
                                  setFoodTypeFilter={setFoodTypeFilter} lang={lang} />
    );
}

/**
 * Switches between the default home layout (food-marketplace style)
 * and one of the iter55 marketplace variants when the active theme
 * preset declares a ``homeLayout``. We keep the food / veg-non-veg
 * filter UI exclusive to the default layout — Amazon / Flipkart /
 * Blinkit-style storefronts don't have a "veg only" toggle.
 */
function MarketplaceHomeOrDefault({ feed, verticals, tenant, filterFood, foodTypeFilter, setFoodTypeFilter, lang }) {
    const presetId = tenant?.branding?.theme_preset;
    const homeLayout = presetId && THEME_PRESETS[presetId]?.homeLayout;
    if (homeLayout) {
        return <MarketplaceHome variant={homeLayout} feed={feed} verticals={verticals} tenant={tenant} />;
    }

    return (
        <div className="pb-32">
            <CustomerHeader subtitle="Banjara Hills">
                <Link to="/search" className="flex items-center justify-between bg-muted rounded-xl px-4 py-3 group active:scale-[0.98] transition">
                    <div className="flex items-center gap-3 text-muted-foreground">
                        <Search size={18} />
                        <span className="text-sm">{t("search_placeholder", lang)}</span>
                    </div>
                    <VoiceSearch />
                </Link>
            </CustomerHeader>

            {/* Quick filters */}
            <div className="flex items-center gap-2 px-4 py-3 overflow-x-auto no-scrollbar">
                <button data-testid="filter-all" onClick={() => setFoodTypeFilter(null)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${!foodTypeFilter ? "bg-foreground text-background border-foreground" : "border-border"}`}>All</button>
                <button data-testid="filter-veg" onClick={() => setFoodTypeFilter("VEG")} className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${foodTypeFilter === "VEG" ? "bg-emerald-50 border-emerald-600 text-emerald-700" : "border-border"}`}>
                    <span className="food-mark veg" /> Veg
                </button>
                <button data-testid="filter-nonveg" onClick={() => setFoodTypeFilter("NON_VEG")} className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${foodTypeFilter === "NON_VEG" ? "bg-red-50 border-red-600 text-red-700" : "border-border"}`}>
                    <span className="food-mark non-veg" /> Non-veg
                </button>
            </div>

            {/* Location Service Engine tenants only (renders nothing otherwise). */}
            <LseHome />

            {/* Admin-curated hero banners (replaces the old 3-tile category grid) */}
            <BannerCarousel banners={feed?.banners || []} />

            {/* iter85 — Group Categories rail. Tenants who set up
                Group Categories see them as a quick-jump strip here;
                if no groups are mapped, the row renders nothing. The
                generic /food landing is not pinned to a vertical so we
                pass no business_category_id, showing every visible
                group. */}
            <GroupCategoryChipRow testIdPrefix="home-group-chips" />

            {/* In-Store Offers tile — entry point to the full browseable
                store-coupons hub (iter43). Always visible: even on a
                fresh tenant it lets the customer see what coupons exist
                across the network. */}
            <section className="px-4 mt-5" data-testid="store-offers-tile">
                <Link
                    to="/store-coupons"
                    className="group relative flex items-center gap-3 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-300/60 rounded-2xl p-4 active:scale-[0.985] transition shadow-sm overflow-hidden"
                >
                    <div className="absolute -right-3 -bottom-3 opacity-10 text-amber-600">
                        <Ticket size={104} strokeWidth={1.2} />
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-amber-500/90 text-white flex items-center justify-center shrink-0 shadow">
                        <Ticket size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700">In-store offers</p>
                        <p className="text-base font-bold leading-tight">Browse coupons near you</p>
                        <p className="text-[11px] text-muted-foreground line-clamp-1">Unlock with coins · use online or show in-store</p>
                    </div>
                    <ChevronRight size={20} className="text-amber-700 group-hover:translate-x-0.5 transition shrink-0" />
                </Link>
            </section>

            {/* Offers */}
            {feed?.coupons?.length > 0 && (
                <section className="px-4 mt-7">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-bold">{t("offers_for_you", lang)}</h2>
                        <Link to="/store-coupons" className="text-xs font-semibold text-primary inline-flex items-center">See all <ChevronRight size={14} /></Link>
                    </div>
                    <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                        {feed.coupons.map((c) => (
                            <div key={c.id} data-testid={`coupon-${c.code}`} className="min-w-[260px] bg-gradient-to-br from-accent/20 to-primary/10 border border-dashed border-primary/40 rounded-2xl p-4">
                                <div className="flex items-center gap-2 text-primary">
                                    <BadgePercent size={18} />
                                    <span className="text-[10px] uppercase tracking-[0.16em] font-bold">Use code</span>
                                </div>
                                <p className="font-display font-bold text-xl mt-1">{c.code}</p>
                                <p className="text-sm font-semibold mt-1">{c.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.description}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Featured */}
            <section className="px-4 mt-7">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold">{t("featured", lang)}</h2>
                </div>
                {!feed ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3"><CardSkeleton /><CardSkeleton /></div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3" data-testid="featured-grid">
                        {filterFood(feed.featured_restaurants).map((r) => <RestaurantCard key={r.id} r={r} compact />)}
                    </div>
                )}
            </section>

            {/* Nearby / All */}
            <section className="px-4 mt-7">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold">{t("popular_near_you", lang)}</h2>
                    <span className="text-xs text-muted-foreground">{filterFood(feed?.nearby_restaurants || []).length} places</span>
                </div>
                {!feed ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5"><CardSkeleton /><CardSkeleton /></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5" data-testid="nearby-list">
                        {filterFood(feed.nearby_restaurants).map((r) => <RestaurantCard key={r.id} r={r} />)}
                    </div>
                )}
            </section>

            <footer className="text-center py-8" data-testid="home-footer">
                <p className="text-sm font-semibold text-foreground/80">{tenant?.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                    Powered by {poweredByBrand(tenant?.branding?.powered_by_text)}
                </p>
            </footer>
        </div>
    );
}
